/**
 * Campi prodotto personalizzati per organizzazione ↔ meta_data WooCommerce.
 *
 * Le definizioni stanno in settings.product_custom_fields (array jsonb per org),
 * i valori in products.custom_fields ({ key: valore }). Questo modulo e' l'unico
 * posto che sa convertire un valore nel formato che il plugin WordPress legge e
 * viceversa: lo usano le edge function (sync-single-item, sync-products-worker,
 * woocommerce-import-product-custom-fields) e il frontend (woocommerceService,
 * scheda prodotto, Impostazioni).
 *
 * Nessuna dipendenza da Deno ne' dal browser: funzioni pure testate da vitest
 * (vedi tsconfig.app.json `include` e src/lib/__tests__/productCustomFields.test.ts).
 *
 * Regole di conversione verso WooCommerce (tutti i valori diventano stringhe):
 * - boolean → 'yes' / ''
 * - number  → '24' (vuoto → '')
 * - pairs   → JSON di coppie [["Varieta'","Carnaroli"],["Peso","1 kg"]] (nessuna riga → '')
 * - select / text / textarea → stringa
 *
 * Si inviano solo le chiavi PRESENTI in products.custom_fields (anche vuote): un
 * prodotto mai toccato in Flusia non azzera i meta gia' presenti in WordPress.
 *
 * Shopify (09/10/2026): `shopify_metafield` ("namespace.chiave", es. custom.brand)
 * e' il metafield su cui va il campo per i negozi Shopify collegati al motore
 * e-commerce. Le conversioni Shopify stanno nel core
 * (flusia-core flusia/catalog/customFields.ts); qui solo forma e validazione,
 * con le stesse regole (namespace 3-255, chiave 2-64, mai flusia/shopify/app--).
 */

export const PRODUCT_CUSTOM_FIELD_TYPES = ['text', 'textarea', 'number', 'boolean', 'select', 'pairs'] as const;
export type ProductCustomFieldType = typeof PRODUCT_CUSTOM_FIELD_TYPES[number];

export const PRODUCT_CUSTOM_FIELD_TABS = ['anagrafica', 'tecnica', 'commerciale', 'logistica'] as const;
export type ProductCustomFieldTab = typeof PRODUCT_CUSTOM_FIELD_TABS[number];

export interface ProductCustomFieldOption {
  value: string;
  label: string;
}

export interface ProductCustomFieldDefinition {
  key: string;
  label: string;
  field_type: ProductCustomFieldType;
  options: ProductCustomFieldOption[];
  /** Chiave meta WooCommerce; null = campo solo interno */
  wc_meta_key: string | null;
  /** Metafield Shopify "namespace.chiave"; null/assente = il campo non va a Shopify */
  shopify_metafield?: string | null;
  tab: ProductCustomFieldTab;
  sort_order: number;
  active: boolean;
  /**
   * Si traduce nelle altre lingue dell'org (sezione «Traduzioni» della scheda,
   * valori in product_translations.custom_fields). Solo text, textarea e pairs;
   * assente = no.
   */
  translatable?: boolean;
}

/** Tipi di campo che hanno senso tradotti: i testi. Numeri, sì/no e selezioni no. */
export const TRANSLATABLE_CUSTOM_FIELD_TYPES: readonly ProductCustomFieldType[] = ['text', 'textarea', 'pairs'];

export const canTranslateFieldType = (type: ProductCustomFieldType) => TRANSLATABLE_CUSTOM_FIELD_TYPES.includes(type);

/** Una riga del tipo `pairs`: [etichetta, valore] */
export type ProductCustomFieldPair = [string, string];
export type ProductCustomFieldValue = string | number | boolean | null | ProductCustomFieldPair[];
export type ProductCustomFieldValues = Record<string, ProductCustomFieldValue>;

export interface WcMetaEntry {
  id?: number;
  key: string;
  value: unknown;
}

/**
 * Chiavi meta che la sincronizzazione scrive gia' da sola o che WooCommerce usa
 * internamente (WC ignora le seconde in scrittura via REST). Un campo
 * personalizzato non puo' usarle: le sovrascriverebbe o non verrebbe salvato.
 */
export const RESERVED_WC_META_KEYS: ReadonlySet<string> = new Set([
  // Scritte dalla sync Flusia → WooCommerce
  '_crm_product_id', '_crm_sync_timestamp', '_barcode', '_video_url', '_cost_price',
  '_crm_price_lists', '_package_contents', '_allow_guest_purchase', '_guest_price_list_id',
  '_product_files', '_crm_category_discount', '_crm_variation_id', '_last_stock_change',
  // Interne di WooCommerce
  '_sku', '_price', '_regular_price', '_sale_price', '_sale_price_dates_from', '_sale_price_dates_to',
  '_stock', '_stock_status', '_manage_stock', '_backorders', '_low_stock_amount', '_sold_individually',
  '_weight', '_length', '_width', '_height', '_tax_status', '_tax_class', '_virtual', '_downloadable',
  '_downloadable_files', '_download_limit', '_download_expiry', '_thumbnail_id', '_product_image_gallery',
  '_product_attributes', '_default_attributes', '_purchase_note', '_product_url', '_button_text',
  '_children', '_upsell_ids', '_crosssell_ids', '_wc_average_rating', '_wc_rating_count',
  '_wc_review_count', '_edit_lock', '_edit_last', 'total_sales',
]);

const KEY_RE = /^[a-z][a-z0-9_]*$/;
const WC_META_KEY_RE = /^[A-Za-z0-9_-]+$/;
// MetafieldsSetInput di Shopify: namespace 3-255, chiave 2-64, lettere, numeri, - e _
const SHOPIFY_METAFIELD_RE = /^[A-Za-z0-9_-]{3,255}\.[A-Za-z0-9_-]{2,64}$/;

/** Namespace riservati: flusia e' del motore, shopify* di Shopify, app--* di altre app. */
const isReservedShopifyNamespace = (ns: string) => {
  const n = ns.toLowerCase();
  return n === 'flusia' || n.startsWith('shopify') || n.startsWith('app--');
};

/** Metafield proposto per un campo nuovo: custom.<chiave> (namespace dei campi del negoziante). */
export function suggestShopifyMetafield(key: string): string {
  return key.trim().length >= 2 ? `custom.${key.trim()}` : '';
}
const TRUE_STRINGS = new Set(['yes', 'true', '1', 'on', 'si', 'sì']);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const hasOwn = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key);

/** "Tempo di cottura" → "tempo_di_cottura" (accenti tolti, max 64). */
export function slugifyFieldKey(label: string): string {
  const slug = String(label ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
  // La chiave deve iniziare con una lettera
  return /^[a-z]/.test(slug) ? slug : slug ? `f_${slug}`.slice(0, 64) : '';
}

/**
 * Legge settings.product_custom_fields in modo difensivo: scarta le voci senza
 * chiave valida, completa i default e ordina per sort_order. Tollera null e
 * colonna assente (migration non ancora eseguita).
 */
export function normalizeProductCustomFieldDefinitions(raw: unknown): ProductCustomFieldDefinition[] {
  if (!Array.isArray(raw)) return [];
  const out: ProductCustomFieldDefinition[] = [];
  raw.forEach((item, index) => {
    if (!isRecord(item)) return;
    const key = typeof item.key === 'string' ? item.key.trim() : '';
    if (!KEY_RE.test(key)) return;
    const fieldType = (PRODUCT_CUSTOM_FIELD_TYPES as readonly string[]).includes(item.field_type as string)
      ? (item.field_type as ProductCustomFieldType)
      : 'text';
    const tab = (PRODUCT_CUSTOM_FIELD_TABS as readonly string[]).includes(item.tab as string)
      ? (item.tab as ProductCustomFieldTab)
      : 'commerciale';
    const options = Array.isArray(item.options)
      ? item.options
          .filter(isRecord)
          .map((o) => ({ value: String(o.value ?? '').trim(), label: String(o.label ?? o.value ?? '').trim() }))
          .filter((o) => o.value !== '')
      : [];
    const wcMetaKey = typeof item.wc_meta_key === 'string' && item.wc_meta_key.trim() ? item.wc_meta_key.trim() : null;
    const shopifyMetafield = typeof item.shopify_metafield === 'string' && item.shopify_metafield.trim() ? item.shopify_metafield.trim() : null;
    out.push({
      key,
      label: typeof item.label === 'string' && item.label.trim() ? item.label.trim() : key,
      field_type: fieldType,
      options,
      wc_meta_key: wcMetaKey,
      shopify_metafield: shopifyMetafield,
      tab,
      sort_order: typeof item.sort_order === 'number' && Number.isFinite(item.sort_order) ? item.sort_order : index,
      active: item.active !== false,
      // Chiave solo se vera: le definizioni senza traduzioni restano identiche a prima
      ...(item.translatable === true && canTranslateFieldType(fieldType) ? { translatable: true } : {}),
    });
  });
  return out.sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * Errori di configurazione (per il salvataggio dalle Impostazioni). Lista vuota
 * = definizioni valide.
 */
export function validateProductCustomFieldDefinitions(defs: ProductCustomFieldDefinition[]): string[] {
  const errors: string[] = [];
  const keys = new Set<string>();
  const metaKeys = new Set<string>();
  const metafields = new Set<string>();
  for (const def of defs) {
    if (!KEY_RE.test(def.key) || def.key.length > 64) {
      errors.push(`Chiave "${def.key}" non valida: solo lettere minuscole, numeri e _ (deve iniziare con una lettera).`);
    }
    if (keys.has(def.key)) errors.push(`Chiave "${def.key}" usata da piu' campi.`);
    keys.add(def.key);
    if (!def.label.trim()) errors.push(`Il campo "${def.key}" non ha un'etichetta.`);
    if (def.field_type === 'select' && def.options.length === 0) {
      errors.push(`Il campo "${def.label}" e' una selezione ma non ha opzioni.`);
    }
    if (def.wc_meta_key) {
      if (!WC_META_KEY_RE.test(def.wc_meta_key) || def.wc_meta_key.length > 191) {
        errors.push(`Chiave meta WooCommerce "${def.wc_meta_key}" non valida: solo lettere, numeri, _ e -.`);
      }
      if (RESERVED_WC_META_KEYS.has(def.wc_meta_key)) {
        errors.push(`La chiave meta "${def.wc_meta_key}" e' gia' usata dalla sincronizzazione o da WooCommerce.`);
      }
      if (metaKeys.has(def.wc_meta_key)) errors.push(`Chiave meta "${def.wc_meta_key}" usata da piu' campi.`);
      metaKeys.add(def.wc_meta_key);
    }
    if (def.shopify_metafield) {
      const mf = def.shopify_metafield;
      if (!SHOPIFY_METAFIELD_RE.test(mf)) {
        errors.push(`Metafield Shopify "${mf}" non valido: scrivilo come namespace.chiave (es. custom.brand), solo lettere, numeri, _ e -.`);
      } else if (isReservedShopifyNamespace(mf.slice(0, mf.indexOf('.')))) {
        errors.push(`Il metafield Shopify "${mf}" usa un namespace riservato: usa per esempio custom.`);
      }
      if (metafields.has(mf)) errors.push(`Metafield Shopify "${mf}" usato da piu' campi.`);
      metafields.add(mf);
    }
  }
  return errors;
}

/** Campi attivi, eventualmente solo quelli di un tab. */
export function activeProductCustomFields(
  defs: ProductCustomFieldDefinition[],
  tab?: ProductCustomFieldTab | string,
): ProductCustomFieldDefinition[] {
  return defs.filter((d) => d.active && (!tab || d.tab === tab));
}

/** Campi attivi con chiave meta: quelli che vanno a (e vengono da) WooCommerce. */
export function syncedProductCustomFields(defs: ProductCustomFieldDefinition[]): ProductCustomFieldDefinition[] {
  return defs.filter((d) => d.active && !!d.wc_meta_key);
}

/** Righe `pairs` pulite: sempre [string, string], righe del tutto vuote tolte. */
export function normalizePairs(raw: unknown): ProductCustomFieldPair[] {
  let value = raw;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return [];
    try {
      value = JSON.parse(s);
    } catch {
      return [];
    }
  }
  // Un array PHP associativo arriva dalla REST API come oggetto
  if (isRecord(value)) value = Object.values(value);
  if (!Array.isArray(value)) return [];
  const rows: ProductCustomFieldPair[] = [];
  for (const row of value) {
    let pair: [unknown, unknown] | null = null;
    if (Array.isArray(row)) pair = [row[0], row[1]];
    else if (isRecord(row)) {
      if ('label' in row || 'value' in row) pair = [row.label ?? row.key, row.value];
      else {
        const vals = Object.values(row);
        pair = [vals[0], vals[1]];
      }
    }
    if (!pair) continue;
    const label = pair[0] == null ? '' : String(pair[0]);
    const val = pair[1] == null ? '' : String(pair[1]);
    if (!label.trim() && !val.trim()) continue;
    rows.push([label, val]);
  }
  return rows;
}

/** Valore Flusia → stringa per meta_data WooCommerce. */
export function toWcMetaValue(type: ProductCustomFieldType, value: unknown): string {
  switch (type) {
    case 'boolean': {
      if (value === true) return 'yes';
      if (typeof value === 'number') return value !== 0 ? 'yes' : '';
      if (typeof value === 'string') return TRUE_STRINGS.has(value.trim().toLowerCase()) ? 'yes' : '';
      return '';
    }
    case 'number': {
      if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
      if (typeof value === 'string') return value.trim();
      return '';
    }
    case 'pairs': {
      const rows = normalizePairs(value);
      return rows.length > 0 ? JSON.stringify(rows) : '';
    }
    default: {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      return JSON.stringify(value);
    }
  }
}

/** meta_data WooCommerce → valore Flusia (conversione inversa). */
export function fromWcMetaValue(type: ProductCustomFieldType, raw: unknown): ProductCustomFieldValue {
  switch (type) {
    case 'boolean': {
      if (raw === true) return true;
      if (typeof raw === 'number') return raw !== 0;
      if (typeof raw === 'string') return TRUE_STRINGS.has(raw.trim().toLowerCase());
      return false;
    }
    case 'number': {
      if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
      if (typeof raw !== 'string' || !raw.trim()) return null;
      const s = raw.trim();
      // "12,5" (virgola decimale) → 12.5; "1.234,5" → 1234.5
      const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
      const n = Number(normalized);
      return Number.isFinite(n) ? n : null;
    }
    case 'pairs':
      return normalizePairs(raw);
    default: {
      if (raw === null || raw === undefined) return '';
      if (typeof raw === 'string') return raw;
      if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
      return JSON.stringify(raw);
    }
  }
}

/**
 * Voci meta_data da inviare a WooCommerce: una per ogni campo attivo con
 * chiave meta la cui chiave e' PRESENTE nei custom_fields del prodotto.
 */
export function buildCustomFieldsMetaData(
  defs: ProductCustomFieldDefinition[],
  customFields: unknown,
): Array<{ key: string; value: string }> {
  if (!isRecord(customFields)) return [];
  const out: Array<{ key: string; value: string }> = [];
  for (const def of syncedProductCustomFields(defs)) {
    if (!hasOwn(customFields, def.key)) continue;
    out.push({ key: def.wc_meta_key as string, value: toWcMetaValue(def.field_type, customFields[def.key]) });
  }
  return out;
}

/**
 * Valori da scrivere in products.custom_fields partendo dai meta_data di un
 * prodotto WooCommerce: solo i campi configurati il cui meta esiste.
 */
export function readCustomFieldsFromWcMeta(
  defs: ProductCustomFieldDefinition[],
  metaData: unknown,
): ProductCustomFieldValues {
  const out: ProductCustomFieldValues = {};
  if (!Array.isArray(metaData)) return out;
  for (const def of syncedProductCustomFields(defs)) {
    const entry = metaData.find((m) => isRecord(m) && m.key === def.wc_meta_key) as WcMetaEntry | undefined;
    if (!entry) continue;
    out[def.key] = fromWcMetaValue(def.field_type, entry.value);
  }
  return out;
}

/**
 * Aggiunge le voci `extra` a `base` sostituendo quelle con la stessa chiave
 * (WooCommerce, ricevendo due voci con la stessa chiave senza id, le
 * aggiungerebbe entrambe).
 */
export function mergeMetaData<T extends { key: string }>(base: T[], extra: T[]): T[] {
  if (extra.length === 0) return base;
  const extraKeys = new Set(extra.map((m) => m.key));
  return [...base.filter((m) => !extraKeys.has(m.key)), ...extra];
}

/**
 * Definizioni dell'org da settings (riga piu' recente, come il resto del
 * codice). Qualsiasi errore — anche la colonna non ancora migrata — vale
 * "nessun campo": la sincronizzazione prosegue come prima.
 */
interface SettingsQuery {
  select: (columns: string) => SettingsQuery;
  eq: (column: string, value: string) => SettingsQuery;
  order: (column: string, options: { ascending: boolean }) => SettingsQuery;
  limit: (count: number) => PromiseLike<{ data: Array<{ product_custom_fields?: unknown }> | null; error: unknown }>;
}

export async function loadProductCustomFieldDefinitions(
  client: { from: (table: string) => unknown },
  organizationId: string,
): Promise<ProductCustomFieldDefinition[]> {
  try {
    const { data, error } = await (client.from('settings') as SettingsQuery)
      .select('product_custom_fields')
      .eq('organization_id', organizationId)
      .order('updated_at', { ascending: false })
      .limit(1);
    if (error) return [];
    return normalizeProductCustomFieldDefinitions(data?.[0]?.product_custom_fields);
  } catch {
    return [];
  }
}

/** Uguaglianza profonda minima per valori jsonb (evita update inutili). */
export function sameCustomFieldValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
